<template>
  <div class="content-wrapper">
    <div class="row">
      <div class="col-sm-12 col-md-6">
        <app-breadcrumb
            :page-title="pageTitle"
            :directory="$t('datatables')"
            :icon="'grid'"
        />
      </div>
    </div>
    <template>
      <div>
        <!-- Step 1: Screening Type Selection -->
        <div v-if="currentStep === 1" class="card card-with-shadow border-0">
          <div class="card-body">
            <h5>Select Screening Type</h5>

            <!-- Warning when only 1 user exists -->
            <div
                v-if="userCount === 1"
                class="alert alert-warning my-3"
                style="font-size: 14px;"
            >
              You currently have only <strong>one user</strong> in your account.
              <br>
              Please <strong>add another user</strong> before creating a screening application.
            </div>

            <el-radio-group v-model="screeningType" class="d-block">
              <div class="my-2">
                <el-radio label="default">Default Screening</el-radio>
              </div>
              <div class="my-2">
                <el-radio label="restricted">Restricted Screening</el-radio>
              </div>
            </el-radio-group>

            <!-- Informational note about auto-assigned forms -->
            <el-alert
                class="mt-4"
                type="info"
                :closable="false"
                show-icon
            >
              <strong>Employment Documents/Contracts Notice</strong>
              <p style="margin: 6px 0 0; font-size: 13px;">
                Once the applicant is marked <em>Fit for Employment</em>, required
                employment forms will be <strong>automatically assigned</strong>.
                You may also add additional documents manually if needed.
              </p>
            </el-alert>

            <!-- Add Employment Form Section -->
            <div class="mt-4 pt-4 border-top">
              <label class="form-label font-weight-bold">Add Employment Form</label>
              <div class="mb-3">
                <a :href="libraryUrl" 
                   target="_blank"
                   class="btn btn-primary shadow-sm px-4 mb-2"
                   style="font-weight: 600; border-radius: 4px;">
                   {{ isAdmin ? 'Go to Library' : 'Go to Form Templates' }}
                </a>
                <p class="small text-muted mb-0">
                  {{ isAdmin ? 'Visit the library to manage and add new employment forms.' : 'Visit Form Templates to manage and add new employment forms.' }}
                </p>
              </div>
            </div>

            <!-- Searchable dropdown -->
            <div class="mt-4">
              <label class="form-label font-weight-bold">
                Add Additional Forms (Optional)
              </label>

              <el-select
                  v-model="selectedDocumentIds"
                  multiple
                  filterable
                  collapse-tags
                  placeholder="Search and select forms"
                  style="width: 100%;"
              >
                <el-option
                    v-for="f in filteredScreeningForms"
                    :key="f.id"
                    :label="f.value"
                    :value="f.id"
                />
              </el-select>

              <small class="text-muted d-block mt-1">
                Selected forms will be added to the employment documentation.
              </small>
            </div>

            <!-- Preview (shows names instead of IDs) -->
            <div v-if="selectedDocumentIds.length" class="mt-3">
              <label class="form-label font-weight-bold">Selected Documents</label>

              <div class="d-flex flex-wrap">
                <el-tag
                    v-for="id in visibleSelectedDocumentIds"
                    :key="id"
                    type="success"
                    effect="plain"
                    class="mr-2 mb-2"
                >
                  <a :href="`/form/preview/${id}`" target="_blank" style="text-decoration: none; color: inherit;">
                    {{ getFormLabel(id) }}
                  </a>
                </el-tag>
              </div>
            </div>


            <!-- Continue button -->
            <div class="mt-4">
              <el-button
                  type="primary"
                  @click="goToNextStep"
                  :disabled="!screeningType || userCount === 1"
              >
                Continue
              </el-button>
            </div>
          </div>


        </div>

        <!-- Step 2: Restricted Options -->
        <div v-if="currentStep === 2 && screeningType === 'restricted'" class="card card-with-shadow border-0">
          <div class="card-body">
            <h5>Customize Screening Options</h5>
            <br>
            <el-switch
                v-model="screeningOptions.siaLicense"
                active-text="Include SIA License Number with Expiry"
                class="d-block my-2"
            />
            <el-switch
                v-model="screeningOptions.addressHistory"
                active-text="Include 5-Year Address History"
                class="d-block my-2"
            />
            <el-switch
                v-model="screeningOptions.employmentHistory"
                active-text="Include 5-Year Employment History"
                class="d-block my-2"
            />
            <el-switch
                v-model="screeningOptions.documents"
                active-text="Include All Required Documents"
                class="d-block my-2"
            />
            <div class="mt-3">
              <el-button @click="goToPreviousStep">Back</el-button>
              <el-button type="primary" @click="goToNextStep">Continue</el-button>
            </div>
          </div>
        </div>

        <!-- Step 3: Main Form (your original form here) -->
        <div v-if="currentStep === 3">
          <div class="card card-with-shadow border-0">
            <div class="card-body">
              <el-button @click="goToPreviousStep" class="mb-3">Back</el-button>
              <!-- 📝 Your existing form starts here -->

              <div class="card card-with-shadow border-0">
                <app-overlay-loader v-if="preloader"/>
                <div class="card-body">
                  <form ref="form"
                        :data-url="formUrl"
                        class="mb-0"
                        :class="{'loading-opacity': preloader}"
                        method="post">
                    <div class="row">
                      <div class="col-sm-6">
                        <div class="form-group row">
                          <label class="h-fit-content font-weight-bold">
                            Email the registration link to given user.
                          </label>
                        </div>
                        <div class="form-group row">
                          <label class="col-sm-3 h-fit-content mt-2">
                            {{ $t('application_first_name') }}
                          </label>
                          <div class="col-sm-6">
                            <app-input type="text"
                                       :required="true"
                                       :min-length="2"
                                       :max-length="20"
                                       id="first_name"
                                       :placeholder="$t('application_first_name')"
                                       v-model="applicationObj.first_name"/>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-sm-3 h-fit-content mt-2">
                            {{ $t('application_last_name') }}
                          </label>
                          <div class="col-sm-6">
                            <app-input type="text"
                                       :required="true"
                                       id="last_name"
                                       :placeholder="$t('application_last_name')"
                                       v-model="applicationObj.last_name"/>
                          </div>
                        </div>
                        <div class="form-group row">
                          <label class="col-sm-3 h-fit-content mt-2">
                            {{ $t('application_email_address') }}
                          </label>
                          <div class="col-sm-6">
                            <app-input type="text"
                                       :required="true"
                                       id="email_address"
                                       :placeholder="$t('application_email_address')"
                                       v-model="applicationObj.email_address"/>
                          </div>
                        </div>
                        <div class="form-group row">
                          <p class="col-sm-10">
                            Please ensure that this field contains the applicant’s email address. Failure to use
                            the applicant’s email address may result in the application being rejected by Disclosure
                            Services or withdrawn by the Disclosure Agency with no refund being given.
                          </p>
                        </div>
                        <div class="mt-2 action-buttons">
                          <button type="submit" class="btn btn-primary mr-2" @click.prevent="submit">
                            {{ $t('Send Invitation') }}
                          </button>
                          <button type="submit" class="btn btn-secondary" @click.prevent="cancel">
                            {{ $t('Cancel') }}
                          </button>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="form-group row">
                          <label class="h-fit-content font-weight-bold">
                            Generate registration link.
                          </label>
                        </div>
                        <div class="form-group row">
                          <div class="col-sm-12">
                            <button type="submit" class="btn btn-success" @click.prevent="generateLink">
                              {{ $t('Generate Candidate Link') }}
                            </button>
                            <div class="col-sm-12 mt-4" >
                            <fragment v-if="publicLink != ''">
                              <app-input type="text" v-model="publicLink" :read-only="true"/>
                            </fragment>
                              </div>
                          </div>
                          <div class="col-sm-12 mt-4"  v-if="publicLink != ''">
                            <button type="submit" class="btn btn-secondary" @click.prevent="copyLink">
                              {{ $t('Copy Link') }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>


  </div>
</template>

<script>
import {CREATE_NEW_APPLICATION} from "@/app/Config/ScreeningAppUrl";
import {FormMixin} from '../../../../../core/mixins/form/FormMixin.js';
import {agencyList, organizationList, positionList} from '@Views/Screening/utility/constant';
import {NewApplication} from "@Views/Screening/utility/apiAgent";
import * as actions from "@/app/Config/ApiUrl";

export default {
  name: "CreateNewApplication",
  mixins: [FormMixin],
  props: {
    userCount: Number,
    currentScreeningDocuments: { type: Array, default: () => [] }, // [893, 43, 6013]
    screeningForms: { type: Array, default: () => [] }, // [{id, value}, ...]
  },
  data() {
    return {

      pageTitle: 'Create New Application',
      applicationObj: {},
      formUrl: CREATE_NEW_APPLICATION,
      preloader: false,
      organizationList: organizationList,
      positionList: positionList,
      agencyList: agencyList,
      publicLink: '',
      currentStep: 1, // Step control
      screeningType: '', // 'default' or 'restricted'
      screeningOptions: {
        siaLicense: false,
        addressHistory: false,
        employmentHistory: false,
        documents: false
      },
      selectedDocumentIds: [...this.currentScreeningDocuments],
    };
  },
  methods: {
    getFormLabel(id) {
      // if currentScreeningDocuments contains ids not present in screeningForms, show fallback
      const form = this.formsById[id] || this.formsById[String(id)] || this.formsById[Number(id)];
      return form?.value || '';
    },
    addToEmploymentContracts() {
      const ids = (this.selectedDocumentIds || []).join() || 0;
      this.axiosPost({
        url: `${actions.ADD_TO_SCREENING_DOCUMENTS}/${ids}?id_type=revision&mode=sync`
      })
          .then((response) => {
            this.$toastr.s(response.data.message);
          })
          .catch(({ error }) => {
            // Trigger after error
          })
          .finally(() => {

          });
    },
    goToNextStep() {
      this.addToEmploymentContracts();
      if (this.userCount === 1) {
        this.$message.warning(
            "You must add at least two users before starting an application."
        );
        return;
      }
      if (this.currentStep === 1 && this.screeningType === 'default') {
        this.currentStep = 3;
      } else if (this.currentStep === 1 && this.screeningType === 'restricted') {
        this.currentStep = 2;
      } else if (this.currentStep === 2) {
        // save screeningOptions if needed
        this.currentStep = 3;
      }
      this.publicLink = '';
    },
    goToPreviousStep() {
      if (this.currentStep === 2) {
        this.currentStep = 1;
      } else if (this.currentStep === 3 && this.screeningType === 'restricted') {
        this.currentStep = 2;
      } else if (this.currentStep === 3 && this.screeningType === 'default') {
        this.currentStep = 1;
      }
    },

    beforeSubmit() {
      this.preloader = true;
    },
    submit() {
      this.applicationObj = {
        ...this.applicationObj,
        screeningOptions: this.screeningOptions, // this will add/overwrite keys from screeningOptions
      };
      this.save(this.applicationObj);
    },
    afterSuccess(res) {
      this.$toastr.s(res.data.message);
    },
    afterFinalResponse() {
      this.preloader = false;
      this.applicationObj = {};
    },
    afterError(res) {
      this.$toastr.e(res.data.message);
    },
    cancel() {
      this.applicationObj = {};
      this.fieldStatus = {
        isSubmit: false
      }
    },
    generateLink() {
      NewApplication.getPublicAppLink(this.screeningOptions)
          .then((response) => {
            this.publicLink = response.data;
            this.$toastr.s('Link Generated.');
          })
    },
    copyLink() {
      if (this.publicLink !== '') {
        const storage = document.createElement('textarea');
        storage.value = this.publicLink;
        document.body.appendChild(storage);
        storage.select();
        storage.setSelectionRange(0, 99999);
        try {
          document.execCommand('copy');
          this.$toastr.s('Link Copied.')
        } catch (err) {
          this.$toastr.e('Failed to copy text.')
          console.error('Failed to copy text: ', err);
        }
        document.body.removeChild(storage);
        return;
      }
      this.$toastr.e('No Link Generated.')
    }
  },
  watch: {
    // if parent updates the prop, sync local selection
    currentScreeningDocuments: {
      deep: true,
      handler(val) {
        this.selectedDocumentIds = [...(val || [])];
      },
    },

    // when user changes selection, notify parent (pick one pattern below)
    selectedDocumentIds: {
      deep: true,
      handler(val) {
        // Option A: emit a custom event
        this.$emit("documents-change", val);

        // Option B: if you want v-model style in parent, use:
        // this.$emit("update:currentScreeningDocuments", val);
      },
    },
  },
  computed: {
    filteredScreeningForms() {
      return (this.screeningForms || []).filter(f => {
        const label = (f?.value || '').trim();
        return label && label.toUpperCase() !== 'HR';
      });
    },
    excludedIdsSet() {
      const set = new Set();
      (this.screeningForms || []).forEach(f => {
        const label = (f?.value || '').trim();
        if (!label || label.toUpperCase() === 'HR') {
          set.add(f.id);
        }
      });
      return set;
    },
    visibleSelectedDocumentIds() {
      return (this.selectedDocumentIds || []).filter(id => !this.excludedIdsSet.has(id) && !!this.getFormLabel(id));
    },
    formsById() {
      // { 6013: {id:6013,value:"..."}, ... }
      return (this.screeningForms || []).reduce((acc, f) => {
        acc[f.id] = f;
        return acc;
      }, {});
    },
    libraryUrl() {
      const user = this.$store.state.user.loggedInUser;
      if (user && user.company_id == 1) {
        return "/admin/documentlibrary/mydocument";
      }
      return "/admin/documentlibrary/document";
    },
    isAdmin() {
      const user = this.$store.state.user.loggedInUser;
      return user && user.company_id == 1;
    },
  },
}
</script>

<style scoped>
/* Hide the first collapsed tag text inside the el-select input;
   keep the +N counter visible */
.el-select .el-select__tags .el-tag {
  display: none;
}

</style>
